import { Button } from '@/components/ui/Button';
import { EmailChipsInput } from '@/components/ui/EmailChipsInput';
import { dashboardRhService } from '../services/dashboard-rh.service';
import { useMutation } from '@tanstack/react-query';
import { Check, Copy, Mail, Share2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  dashboardUuid: string;
  folderName: string;
}

export function ShareModal({
  isOpen,
  onClose,
  dashboardUuid,
  folderName,
}: ShareModalProps) {
  const { t } = useTranslation();
  const [shareMethod, setShareMethod] = useState<'public' | 'email'>('public');
  const [emails, setEmails] = useState<string[]>([]);
  const [shareUrl, setShareUrl] = useState<string>('');
  const [copied, setCopied] = useState(false);

  const shareMutation = useMutation({
    mutationFn: (method: 'public' | 'email') =>
      dashboardRhService.shareDashboard(
        dashboardUuid,
        method,
        method === 'email' ? emails : undefined
      ),
    onSuccess: data => {
      if (data.shareUrl) {
        setShareUrl(data.shareUrl);
      }
    },
  });

  const handleShare = () => {
    shareMutation.mutate(shareMethod);
  };

  const handleCopyUrl = async () => {
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setShareMethod('public');
    setEmails([]);
    setShareUrl('');
    setCopied(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-neutral-900/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
              <Share2 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-neutral-900">
                {t('dashboardRh.share.title', 'Partager le tableau de bord')}
              </h2>
              <p className="text-sm text-neutral-500">{folderName}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Share Method Selection */}
        <div className="mb-6 space-y-3">
          <button
            onClick={() => setShareMethod('public')}
            className={`w-full rounded-lg border-2 p-4 text-left transition-colors ${
              shareMethod === 'public'
                ? 'border-primary-500 bg-primary-50'
                : 'border-neutral-200 bg-white hover:border-neutral-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                  shareMethod === 'public'
                    ? 'bg-primary-100'
                    : 'bg-neutral-100'
                }`}
              >
                <Share2
                  className={`h-5 w-5 ${
                    shareMethod === 'public' ? 'text-primary-600' : 'text-neutral-600'
                  }`}
                />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-neutral-900">
                  {t('dashboardRh.share.public', 'Lien public')}
                </h3>
                <p className="text-sm text-neutral-600">
                  {t(
                    'dashboardRh.share.publicDescription',
                    'Créer un lien partageable accessible sans authentification'
                  )}
                </p>
              </div>
            </div>
          </button>

          <button
            onClick={() => setShareMethod('email')}
            className={`w-full rounded-lg border-2 p-4 text-left transition-colors ${
              shareMethod === 'email'
                ? 'border-primary-500 bg-primary-50'
                : 'border-neutral-200 bg-white hover:border-neutral-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                  shareMethod === 'email'
                    ? 'bg-primary-100'
                    : 'bg-neutral-100'
                }`}
              >
                <Mail
                  className={`h-5 w-5 ${
                    shareMethod === 'email' ? 'text-primary-600' : 'text-neutral-600'
                  }`}
                />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-neutral-900">
                  {t('dashboardRh.share.email', 'Par email')}
                </h3>
                <p className="text-sm text-neutral-600">
                  {t(
                    'dashboardRh.share.emailDescription',
                    'Envoyer le tableau de bord par email'
                  )}
                </p>
              </div>
            </div>
          </button>
        </div>

        {/* Email Input (if email method) */}
        {shareMethod === 'email' && (
          <div className="mb-6">
            <EmailChipsInput
              emails={emails}
              onChange={setEmails}
              label={t('dashboardRh.share.recipients', 'Destinataires')}
              placeholder="email@example.com"
            />
          </div>
        )}

        {/* Share URL (if public method and generated) */}
        {shareMethod === 'public' && shareUrl && (
          <div className="mb-6">
            <label className="mb-2 block text-sm font-medium text-neutral-700">
              {t('dashboardRh.share.link', 'Lien de partage')}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={shareUrl}
                readOnly
                className="flex-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm"
              />
              <button
                onClick={handleCopyUrl}
                className="flex items-center gap-2 rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 text-success-600" />
                    {t('common.copied', 'Copié')}
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    {t('common.copy', 'Copier')}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Success Message */}
        {shareMutation.isSuccess && (
          <div className="mb-6 rounded-lg bg-success-50 p-4">
            <p className="text-sm text-success-800">
              {shareMutation.data?.message}
            </p>
          </div>
        )}

        {/* Error Message */}
        {shareMutation.isError && (
          <div className="mb-6 rounded-lg bg-error-50 p-4">
            <p className="text-sm text-error-800">
              {t('dashboardRh.share.error', 'Erreur lors du partage')}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleClose} className="flex-1">
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button
            onClick={handleShare}
            disabled={
              shareMutation.isPending ||
              (shareMethod === 'email' && emails.length === 0)
            }
            className="flex-1"
          >
            {shareMutation.isPending
              ? t('common.loading', 'Chargement...')
              : shareMethod === 'public' && shareUrl
                ? t('dashboardRh.share.createNew', 'Créer un nouveau lien')
                : t('dashboardRh.share.share', 'Partager')}
          </Button>
        </div>
      </div>
    </div>
  );
}

